import { useState, useEffect } from 'react';
import { getKidsDetailSheets, saveKidsDetailSheets } from '../../utils/api';
import { BrandSelect } from './kidsUi';

// Detail Sheets — live Google Doc embeds, categorized by program + session.
// A session can hold multiple docs (activity sheet + party sheet + ...).
// Everyone can view; staff add/edit/remove the links. Drive links are live, so
// edits made in the Doc show up here automatically.

const PROGRAMS = ['Elementary', '5-6', '3-4', 'All Programs'];

// Turn a Drive share link into an embeddable /preview URL.
function embedUrl(url) {
  if (!url) return url;
  return url
    .replace(/\/edit(\?[^#]*)?/, '/preview')
    .replace(/\/view(\?[^#]*)?/, '/preview');
}

function newId() {
  return Math.random().toString(36).slice(2, 10);
}

export default function KidsDetailSheets({ showToast, readOnly }) {
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null); // {} = new, or existing sheet
  const [confirmRemove, setConfirmRemove] = useState(null);

  useEffect(() => {
    getKidsDetailSheets()
      .then(d => setSheets(Array.isArray(d) ? d : []))
      .catch(() => showToast('Could not load detail sheets.'))
      .finally(() => setLoading(false));
  }, [showToast]);

  async function persist(next) {
    setSheets(next);
    try { await saveKidsDetailSheets(next); }
    catch (e) { showToast('Save failed — try again.'); }
  }

  async function handleSave(form) {
    if (!form.url.trim()) { showToast('A Google Drive link is required.'); return; }
    if (!form.session.trim()) { showToast('Session is required (e.g. Thursday PM).'); return; }
    const entry = {
      id: form.id || newId(),
      program: form.program || 'All Programs',
      session: form.session.trim(),
      label: form.label.trim() || `${form.session.trim()} Detail Sheet`,
      url: form.url.trim(),
    };
    const next = form.id ? sheets.map(s => s.id === form.id ? entry : s) : [...sheets, entry];
    await persist(next);
    setEditing(null);
    showToast('Detail sheet saved.');
  }

  async function handleRemove(id) {
    await persist(sheets.filter(s => s.id !== id));
    setConfirmRemove(null);
    if (viewing?.id === id) setViewing(null);
    showToast('Detail sheet removed.');
  }

  if (viewing) {
    return (
      <div className="page-content">
        <div className="page-title-row">
          <div>
            <div className="page-title">{viewing.label}</div>
            <div className="page-sub">{viewing.program} · {viewing.session} · live Google Doc</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <a className="btn btn-sm" href={viewing.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>Open in Drive ↗</a>
            <button className="btn btn-sm" onClick={() => setViewing(null)}>← Back</button>
          </div>
        </div>
        <div className="pdf-viewer-wrap">
          <iframe src={embedUrl(viewing.url)} className="pdf-viewer-frame" style={{ minHeight: 640, borderRadius: 'var(--radius)' }} allow="autoplay" title={viewing.label} />
        </div>
      </div>
    );
  }

  const programsWithSheets = PROGRAMS.filter(p => sheets.some(s => s.program === p));

  return (
    <div className="page-content">
      <div className="page-title-row">
        <div>
          <div className="page-title">Detail Sheets</div>
          <div className="page-sub">Google Docs, by program &amp; session · a session can hold multiple sheets</div>
        </div>
        {!readOnly && <button className="btn btn-dark" onClick={() => setEditing({})}>+ Add Detail Sheet</button>}
      </div>

      {loading ? (
        <div className="panel" style={{ padding: '32px 24px', textAlign: 'center', color: 'var(--muted)', fontFamily: 'var(--font-b)', fontWeight: 300 }}>Loading…</div>
      ) : sheets.length === 0 ? (
        <div className="panel" style={{ padding: '32px 24px', textAlign: 'center', color: 'var(--muted)', fontFamily: 'var(--font-b)', fontWeight: 300 }}>
          No detail sheets yet{readOnly ? '.' : ' — click + Add Detail Sheet and paste a Google Drive link.'}
        </div>
      ) : programsWithSheets.map(program => {
        // Group this program's sheets by session so multiples sit together.
        const bySession = [];
        sheets.filter(s => s.program === program).forEach(s => {
          const found = bySession.find(x => x.session === s.session);
          if (found) found.docs.push(s); else bySession.push({ session: s.session, docs: [s] });
        });
        return (
          <div key={program} style={{ marginBottom: 24 }}>
            <div className="section-header">{program}</div>
            <div className="panel">
              {bySession.map((grp, gi) => (
                <div key={grp.session} style={{ borderBottom: gi < bySession.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <div style={{ padding: '12px 24px 4px', fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 800, color: 'var(--black)' }}>{grp.session}</div>
                  {grp.docs.map(doc => (
                    <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 24px 12px' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--mid)' }}>{doc.label}</div>
                      </div>
                      <button className="btn btn-xs" onClick={() => setViewing(doc)}>Open</button>
                      {!readOnly && <button className="btn btn-xs" onClick={() => setEditing(doc)}>Edit</button>}
                      {!readOnly && (confirmRemove === doc.id ? (
                        <>
                          <button className="btn btn-xs" onClick={() => setConfirmRemove(null)}>Cancel</button>
                          <button className="btn btn-xs btn-danger" onClick={() => handleRemove(doc.id)}>Remove</button>
                        </>
                      ) : (
                        <button className="btn btn-xs btn-danger" onClick={() => setConfirmRemove(doc.id)}>Remove</button>
                      ))}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {editing !== null && (
        <SheetModal
          sheet={editing}
          onClose={() => setEditing(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

function SheetModal({ sheet, onClose, onSave }) {
  const [form, setForm] = useState({
    id: sheet.id || '',
    program: sheet.program || 'Elementary',
    session: sheet.session || '',
    label: sheet.label || '',
    url: sheet.url || '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="modal-overlay open" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">{form.id ? 'Edit Detail Sheet' : 'Add Detail Sheet'}</div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="two-col" style={{ marginBottom: 0 }}>
            <div className="form-group">
              <label className="form-label">Program</label>
              <BrandSelect value={form.program} options={PROGRAMS} onChange={v => set('program', v)} width="100%" />
            </div>
            <div className="form-group">
              <label className="form-label">Session</label>
              <input className="form-input" placeholder="e.g. Thursday PM" value={form.session} onChange={e => set('session', e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Name</label>
            <input className="form-input" placeholder="e.g. Party Detail Sheet" value={form.label} onChange={e => set('label', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Google Drive Link</label>
            <input className="form-input" placeholder="https://docs.google.com/document/d/…" value={form.url} onChange={e => set('url', e.target.value)} />
            <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)', marginTop: 5 }}>
              Paste the share link — the doc stays live, so edits in Drive show up here automatically.
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-dark" onClick={() => onSave(form)}>{form.id ? 'Save Changes' : 'Add Sheet'}</button>
        </div>
      </div>
    </div>
  );
}
